import React, {Fragment} from "react";
import {IMAGES_URL} from "data/constants";
import {PlayerImageType, PlayerCategory} from "data/enums";
import {PlayerAvatar} from "views/components/players/player_avatar/player_avatar.component";
import {Stack, Typography} from "@mui/material";
import {observer} from "mobx-react";
import {useViewController} from "data/services/locator";
import {Bindings} from "data/constants/bindings";
import {IPlayerStatsController} from "views/controllers/player_stats/player_stats.controller";
import {
	PlayerStatsWrapper,
	ImageWrapp,
	SnowAnimationPosition,
} from "views/components/players/player_stats/styles";
import {PlayerCategoryArc} from "views/components/players/player_category_image/player_category_image.component";
import styled from "@emotion/styled";
import {Variants, motion} from "framer-motion";
import {TournamentStatus} from "data/enums/torunaments";
import {MultiplierBadge} from "views/components/players/multiplier_badge/multiplier_badge.component";
import {RosterPlayerTournamentInfo} from "views/components/roster/roster_player_tournament_info/roster_player_tournament_info.component";
import {SnowAnimation} from "views/components/snow_animation/snow_animation.component";
import {Exist} from "views/components/exist/exist.component";

const Stats = styled.div`
	border-radius: 8px;
	background: #090b0f;
	width: 100%;
	overflow: hidden;
	margin-top: 12px;
`;

const StatsHeader = styled.div`
	padding: 8px 16px;
	border-bottom: 1px solid #848990;
	background: #282f38;
	display: flex;
	align-items: center;
	justify-content: space-between;
`;

const StatsFooter = styled(motion.div)`
	padding: 8px 16px;
	background: #282f38;
	display: flex;
	align-items: center;
	justify-content: space-between;
`;

const PointsWrapper = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
`;

const Points = styled(Typography)`
	font-weight: 800;
	color: #99fe88;

	&.${TournamentStatus.COMPLETE} {
		color: var(--primaryColor);
	}
`;

Points.defaultProps = {
	variant: "h3",
};

const MatchInfo = styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
`;

const Row = styled(motion.div)`
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 8px 16px;
	border-bottom: 1px solid var(--Brand-Neutrals-Dark-Grey, #282f38);
`;

export const MainStats = styled(motion.div)`
	flex-flow: column;
	display: flex;
	max-height: 500px;
	overflow-x: hidden;
	overflow-y: auto;
`;

const parent: Variants = {
	hidden: {
		opacity: 0,
	},
	show: {
		opacity: 1,
		transition: {
			staggerChildren: 0.05,
		},
	},
};

const item: Variants = {
	hidden: {
		x: -10,
		opacity: 0,
	},
	show: {
		x: 0,
		opacity: 1,
	},
};

interface IProps {
	playerId: number;
	contestId: number;
}

export const PlayerStatsScoring: React.FC<IProps> = observer(({playerId, contestId}) => {
	const {
		player,
		i18n,
		versus,
		opponentSquadName,
		statsPlayerByContest,
		totalPoints,
		tournamentStatus,
	} = useViewController<IPlayerStatsController>(Bindings.PlayerStatsController, {
		playerID: playerId,
		contestID: contestId,
	});

	if (!player) {
		return null;
	}

	return (
		<Fragment>
			<Exist when={player.contestStats?.category === PlayerCategory.COLD}>
				<SnowAnimationPosition className={"animation"}>
					<SnowAnimation animate={true} infinity={true} />
				</SnowAnimationPosition>
			</Exist>

			<PlayerStatsWrapper key={player?.id}>
				{player.squad?.logo && (
					<img
						src={`${IMAGES_URL}/${player.squad.logo}`}
						alt="Squad Logo"
						role="presentation"
						onError={(e) => (e.currentTarget.style.display = "none")}
					/>
				)}
				<ImageWrapp>
					<PlayerAvatar type={PlayerImageType.headshot} player={player} />
					<PlayerCategoryArc
						category={player.contestStats?.category}
						hideMiddleIcon={true}
						animate={true}
					/>
				</ImageWrapp>

				<Stack>
					<Typography variant={"h3"}>
						{player.firstName} {player.lastName}
					</Typography>
					<Stack direction={"row"} gap={"3px"} justifyContent={"center"}>
						<Typography variant={"miscRegular"}>
							{i18n.t(`player.position.full.${player.position}`, player.position)}
						</Typography>
						<MultiplierBadge multiplier={player.contestStats?.pointsMultiplier} />
					</Stack>
				</Stack>

				<Stats>
					<StatsHeader>
						<Typography variant={"body2"}>Score Breakdown</Typography>
						<MatchInfo>
							<RosterPlayerTournamentInfo playerId={playerId} />
							<Typography variant={"miscRegular"}>
								{versus} {opponentSquadName}
							</Typography>
						</MatchInfo>
					</StatsHeader>
					<MainStats variants={parent} initial={"hidden"} animate={"show"}>
						{statsPlayerByContest.map((it, index) => {
							return (
								<Row variants={item} key={index}>
									<Typography variant={"body2"} sx={{fontWeight: 400}}>
										{it.name}
									</Typography>
									<Points className={tournamentStatus}>{it.value}</Points>
								</Row>
							);
						})}
					</MainStats>
					<StatsFooter variants={parent} initial={"hidden"} animate={"show"}>
						<Typography variant={"body2"}>
							{i18n.t("player.stats.total_daily", "Total Daily Pts")}
						</Typography>
						<PointsWrapper>
							<MultiplierBadge multiplier={player.contestStats?.pointsMultiplier} />
							<Points className={tournamentStatus}>{totalPoints || "--"}</Points>
						</PointsWrapper>
					</StatsFooter>
				</Stats>
			</PlayerStatsWrapper>
		</Fragment>
	);
});
